import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'

interface LegalPageProps {
    title: string
    intro?: ReactNode
    updated?: string
    children: ReactNode
}

const proseClass = [
    'text-[1.02rem] leading-relaxed text-muted-foreground',
    '[&_h2]:mt-12 [&_h2]:text-xl [&_h2]:font-medium [&_h2]:tracking-[-0.01em] [&_h2]:text-foreground [&_h2:first-child]:mt-0',
    '[&_h3]:mt-8 [&_h3]:font-medium [&_h3]:text-foreground',
    '[&_p]:mt-4 [&_ul]:mt-4 [&_ul]:grid [&_ul]:list-disc [&_ul]:gap-2 [&_ul]:pl-5 [&_li]:pl-1 [&_li]:marker:text-primary',
    '[&_a]:font-medium [&_a]:text-foreground [&_a]:underline [&_a]:decoration-border [&_a]:underline-offset-4 [&_a:hover]:decoration-primary',
    '[&_strong]:font-medium [&_strong]:text-foreground'
].join(' ')

export const LegalPage: FC<LegalPageProps> = ({
    title,
    intro,
    updated,
    children
}): ReactNode => {
    return (
        <section
            id='rechtliches'
            aria-labelledby='legal-title'
            className='relative overflow-hidden'
        >
            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-x-0 top-0 h-80 bg-grid [mask-image:linear-gradient(to_bottom,black,transparent)]'
            />

            <div className='relative mx-auto max-w-3xl px-6 pb-24 pt-14 sm:pt-20'>
                <Link
                    href='/'
                    className='inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground'
                >
                    <ArrowLeft className='size-4' aria-hidden='true' />
                    Zur Startseite
                </Link>

                <p className='mt-10 flex items-center gap-3 font-mono text-[0.72rem] font-medium uppercase tracking-[0.2em] text-primary'>
                    <span className='h-px w-6 bg-current' aria-hidden='true' />
                    Rechtliches
                </p>

                <h1
                    id='legal-title'
                    className='mt-5 text-balance text-4xl font-light tracking-[-0.025em] text-foreground sm:text-5xl'
                >
                    {title}
                </h1>

                {intro && (
                    <p className='mt-5 text-pretty text-lg leading-relaxed text-muted-foreground'>
                        {intro}
                    </p>
                )}

                {updated && (
                    <p className='mt-4 font-mono text-xs uppercase tracking-[0.16em] text-muted-foreground'>
                        {updated}
                    </p>
                )}

                <div className='mt-12 rounded-[1.75rem] border border-border bg-card p-6 sm:p-10'>
                    <div className={proseClass}>
                        {children}
                    </div>
                </div>
            </div>
        </section>
    )
}
